import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import Popover from '@mui/material/Popover'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { DateRangePicker } from 'react-date-range'
import { subDays, format, parseISO } from 'date-fns'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { filterButtonSx } from '../../common/sharedStyles'
import { HISTORY_MAX_DAYS, dayStr, quickRanges } from './alertUtils'
import { BAR_BG, LINE, PRIMARY, PRIMARY_LIGHT, PRIMARY_MID, btnSx, inputSx } from './ui'

const STATUSES = [{ value: '', label: 'Any status' }, { value: 'success', label: 'Accepted' }, { value: 'failed', label: 'Failed' }]
const DELIVERY = [{ value: '', label: 'Any receipt' }, { value: 'Sent', label: 'Sent' }, { value: 'Delivered', label: 'Delivered' }, { value: 'Read', label: 'Read' }]

/**
 * filters = { templateNames: [], from: 'yyyy-MM-dd', to: 'yyyy-MM-dd', status: '', currentStatus: '', mobile: '' }
 * `mobile` holds the recipient filter in either form (mobile or email — the tab sends it as `recipient`).
 * Dates are IST calendar days, inclusive; the backend defaults to the last 7 days and caps at 400.
 * The receipt filter only exists for WhatsApp (SES / SMS give no receipts).
 */
export default function HistoryFilters({ filters, onChange, defs = [], channel = 'WHATSAPP', view, onViewChange, disabled, onReset }) {
  const alertOptions = defs.map(d => ({ label: d.displayName || d.id, value: d.id }))
  const wa = channel === 'WHATSAPP'
  const email = channel === 'EMAIL'

  const [anchorEl, setAnchorEl] = useState(null)
  const [range, setRange] = useState([{ startDate: parseISO(filters.from), endDate: parseISO(filters.to), key: 'selection' }])

  const openDates = (e) => {
    setRange([{ startDate: parseISO(filters.from), endDate: parseISO(filters.to), key: 'selection' }])
    setAnchorEl(e.currentTarget)
  }
  const applyDates = () => {
    setAnchorEl(null)
    onChange({ ...filters, from: dayStr(range[0].startDate), to: dayStr(range[0].endDate) })
  }

  const staticRanges = quickRanges().map(r => ({
    label: r.label,
    range: () => ({ startDate: r.from, endDate: r.to }),
    isSelected: (sel) => dayStr(sel.startDate) === dayStr(r.from) && dayStr(sel.endDate) === dayStr(r.to),
  }))

  const dateLabel = filters.from === filters.to
    ? format(parseISO(filters.from), 'dd MMM yyyy')
    : `${format(parseISO(filters.from), 'dd MMM')} – ${format(parseISO(filters.to), 'dd MMM yyyy')}`

  const set = (patch) => onChange({ ...filters, ...patch })
  const hasFilter = (filters.templateNames || []).length > 0 || filters.status || filters.currentStatus || (filters.mobile || '').trim()

  return (
    <Box sx={{ background: BAR_BG, px: 2, py: 1.1, border: `1px solid ${LINE}`, borderRadius: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
      <FilterAltOutlinedIcon sx={{ fontSize: 16, color: '#bbb', flexShrink: 0 }} />
      <ToggleButtonGroup size='small' exclusive value={view} onChange={(_, v) => v && onViewChange(v)} disabled={disabled}
        sx={{ background: '#fff', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontSize: '0.75rem', fontWeight: 600 }, '& .Mui-selected': { color: `${PRIMARY} !important`, background: `${PRIMARY_LIGHT} !important` } }}>
        <ToggleButton value='runs'>By run</ToggleButton>
        <ToggleButton value='messages'>Messages</ToggleButton>
      </ToggleButtonGroup>

      <Autocomplete
        multiple size='small' limitTags={1} disableCloseOnSelect
        options={alertOptions}
        value={alertOptions.filter(o => (filters.templateNames || []).includes(o.value))}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        getOptionLabel={(o) => o.label}
        onChange={(_, sel) => set({ templateNames: sel.map(o => o.value) })}
        renderInput={(params) => <TextField {...params} placeholder='All alerts' sx={inputSx} />}
        sx={{ minWidth: 260 }} disabled={disabled}
      />

      <Button variant='outlined' size='small' startIcon={<CalendarMonthOutlinedIcon />} onClick={openDates} sx={{ ...btnSx, background: '#fff' }} disabled={disabled}>
        {dateLabel}
      </Button>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1 }}>
          <DateRangePicker ranges={range} onChange={(item) => setRange([item.selection])} maxDate={new Date()} minDate={subDays(new Date(), HISTORY_MAX_DAYS)} staticRanges={staticRanges} inputRanges={[]} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
            <Button size='small' sx={filterButtonSx('reset')} onClick={() => setAnchorEl(null)}>Cancel</Button>
            <Button size='small' variant='contained' sx={filterButtonSx('apply')} onClick={applyDates}>Apply</Button>
          </Box>
        </Box>
      </Popover>

      <TextField select size='small' label='Status' value={filters.status || ''} onChange={(e) => set({ status: e.target.value })} sx={{ ...inputSx, width: 140 }} disabled={disabled}>
        {STATUSES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontSize: '0.8rem' }}>{s.label}</MenuItem>)}
      </TextField>
      {wa && (
        <TextField select size='small' label='Receipt' value={filters.currentStatus || ''} onChange={(e) => set({ currentStatus: e.target.value })} sx={{ ...inputSx, width: 140 }} disabled={disabled || filters.status === 'failed'}>
          {DELIVERY.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontSize: '0.8rem' }}>{s.label}</MenuItem>)}
        </TextField>
      )}
      <TextField size='small' placeholder={email ? 'Recipient email' : 'Recipient mobile'} value={filters.mobile || ''} onChange={(e) => set({ mobile: e.target.value })} sx={{ ...inputSx, width: email ? 220 : 170 }} disabled={disabled}
        inputProps={email ? { maxLength: 120, autoCapitalize: 'none' } : { inputMode: 'tel', maxLength: 15 }} />

      <Box sx={{ flex: 1 }} />
      <Box onClick={disabled ? undefined : onReset} sx={{
        display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
        color: hasFilter ? PRIMARY : '#ccc', px: 1.25, py: 0.5, borderRadius: '8px',
        border: `1px solid ${hasFilter ? PRIMARY_MID : '#DFE3E8'}`, background: hasFilter ? PRIMARY_LIGHT : 'transparent', transition: 'all 0.15s',
        '&:hover': { background: PRIMARY_LIGHT, color: PRIMARY, borderColor: PRIMARY_MID },
      }}>
        <RestartAltIcon sx={{ fontSize: 14 }} /> Reset
      </Box>
    </Box>
  )
}
